import { ApiError, api } from '@/components/Functional/lib/request'
import { logger } from '@/components/Functional/utils/logger'
import {
  OcrStampAcquireErrorBodySchema,
  QuizAnswerResponseSchema,
  StampAcquireResponseSchema,
  StampGetResponseSchema,
  StampQuizListResponseSchema,
} from '@/schemas/v2'
import type { QuizAnswerResponse, StampAcquireResponse, StampGetResponse, StampQuizListResponse } from '@/schemas/v2'

const BASE = process.env.NEXT_PUBLIC_API_URL ?? ''

type FetchOptions = { headers?: Record<string, string> }

export async function fetchStamp(stampId: number, options?: FetchOptions): Promise<StampGetResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return StampGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchStampQuizzes(stampId: number, options?: FetchOptions): Promise<StampQuizListResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}/quizzes`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return StampQuizListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postStamp(
  stampId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<StampAcquireResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return StampAcquireResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/**
 * XState 向けの POST。エラー時は ApiError(status, code) が throw される。
 * 成功レスポンスは使用しないため Promise<void> で返す。
 */
export async function postStampStrict(
  stampId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<void> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    await api.post<void>(url, body ?? {}, options)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postStampQuizAnswer(
  stampId: number,
  quizId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<QuizAnswerResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}/quizzes/${quizId}/answer`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return QuizAnswerResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/**
 * OCR スタンプ獲得失敗（読み取り不能）を表すエラー。
 * XState の onError ガードで name === 'OcrStampError' を判定する。
 */
export class OcrStampError extends Error {
  constructor(
    public readonly ocrId: number | null,
    public readonly code?: string,
  ) {
    super('OCR stamp acquisition failed')
    this.name = 'OcrStampError'
  }
}

/**
 * OCR スタンプ獲得。
 * - 成功: StampAcquireResponse を返す
 * - 400 (読み取り失敗): OcrStampError を throw (ocrId を含む)
 * - その他エラー: ApiError を throw
 */
export async function postOcrStamp(
  stampId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<StampAcquireResponse> {
  const url = `${BASE}/api/v2/stamps/${stampId}`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'OCR stamp fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return StampAcquireResponseSchema.parse(data)
  } catch (e) {
    if (e instanceof ApiError && e.status === 400) {
      const parsed = OcrStampAcquireErrorBodySchema.safeParse(e.body)
      const ocrId = parsed.success ? (parsed.data.ocrId ?? null) : null
      const code = parsed.success ? parsed.data.code : undefined
      logger.error({ fetch_method: 'POST', fetch_url: url, msg: `OCR 読み取り失敗 ocrId=${ocrId}` })
      throw new OcrStampError(ocrId, code)
    }
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}
